import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import WhatsAppWidget from '../../components/ui/WhatsAppWidget';
import NewsletterSubscription from '../../components/ui/NewsletterSubscription';
import ConsultationBooking from '../../components/ui/ConsultationBooking';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import SocialMediaLinks from './components/SocialMediaLinks';
import PartnershipInquiry from './components/PartnershipInquiry';
import DonationSupport from './components/DonationSupport';

const ContactHub = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Hub - Edrian Bass | IT Engineer, Entrepreneur & Creative Director</title>
        <meta 
          name="description" 
          content="Connect with Edrian Bass for IT engineering services, business consulting, mentorship programs, and creative direction. Multiple contact channels across Nigeria, Cameroon, and South Africa." 
        />
        <meta 
          name="keywords" 
          content="contact Edrian Bass, IT consulting Nigeria, web development services, business mentorship, creative direction, partnership opportunities" 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <WhatsAppWidget />

        <main className="pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12 lg:mb-16">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                Let's Connect & Collaborate
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
                Whether you need IT engineering expertise, business consulting, mentorship, or creative direction - I'm here to help bring your vision to life across multiple communication channels.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
              <div className="lg:col-span-2">
                <ContactForm />
              </div>
              <div className="space-y-6 md:space-y-8">
                <ConsultationBooking />
                <div className="bg-card rounded-xl p-6 shadow-lg border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Quick Response</h3>
                      <p className="text-sm text-muted-foreground">Available 24/7</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get instant responses via WhatsApp for urgent inquiries and quick consultations.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Average response time: &lt; 2 hours</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8 md:mb-12">
              <ContactInfo />
            </div>

            <div className="mb-8 md:mb-12">
              <SocialMediaLinks />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
              <PartnershipInquiry />
              <DonationSupport />
            </div>

            <div className="mb-8 md:mb-12">
              <NewsletterSubscription />
            </div>

            <div className="bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 rounded-xl p-6 md:p-8 lg:p-12 text-center border border-primary/20">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
                Ready to Start Your Project?
              </h2>
              <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto">
                Let's discuss how I can help transform your ideas into reality through innovative IT solutions, strategic business guidance, and creative excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:infoedrianbass@gmail.com"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email Me Directly</span>
                </a>
                <a
                  href="tel:+2349160085052"
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-card text-foreground rounded-lg font-semibold hover:bg-muted transition-all border border-border"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Call Now</span>
                </a>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground text-center md:text-left">
                © {new Date()?.getFullYear()} Edrian Bass. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="/home-landing" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Home
                </a>
                <a href="/about-me" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  About
                </a>
                <a href="/services-portfolio" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Services
                </a>
                <a href="/blog-insights" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Blog
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactHub;